<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Tipo de Pagamento' : 'Novo Tipo de Pagamento' }}
      </h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>

    <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarTipo" @cancel="voltar">
      <FormSection title="Informações do Tipo">
        <!-- Linha com os selects para Operador, Meio e Forma -->
        <div class="d-flex gap-3 mb-3">
          <!-- Campo Operador -->
          <div class="flex-fill larger-field">
            <SelectField
              id="operador_id"
              v-model="tipo.operador_id"
              :options="operadoresOptions"
              placeholder="Selecione o operador"
              label="Operador <span class='required'>*</span>"
              required
              @update:modelValue="onOperadorChange"
            />
            <div v-if="errors.operador_id" class="error-message">{{ errors.operador_id }}</div>
          </div>
          <!-- Campo Meio -->
          <div class="flex-fill larger-field">
            <SelectField
              id="meio_id"
              v-model="tipo.meio_id"
              :options="meiosOptions"
              placeholder="Selecione o meio"
              label="Meio <span class='required'>*</span>"
              required
              @update:modelValue="onMeioChange"
            />
            <div v-if="errors.meio_id" class="error-message">{{ errors.meio_id }}</div>
          </div>
          <!-- Campo Forma -->
          <div class="flex-fill larger-field">
            <SelectField
              id="forma_id"
              v-model="tipo.forma_id"
              :options="formasOptions"
              placeholder="Selecione a forma"
              label="Forma <span class='required'>*</span>"
              required
            />
            <div v-if="errors.forma_id" class="error-message">{{ errors.forma_id }}</div>
          </div>
          <!-- Campo Tipo de Pagamento -->
          <div class="mb-3">
            <InputField
              id="tipo"
              v-model="tipo.tipo"
              placeholder="Digite o tipo de pagamento"
              label="Tipo de Pagamento <span class='required'>*</span>"
              required
              :class="{ 'error-input': errors.tipo }"
            />
            <div v-if="errors.tipo" class="error-message">{{ errors.tipo }}</div>
          </div>
        </div>
      </FormSection>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import InputField from "@/components/Layout/InputField.vue";
import SelectField from "@/components/Layout/SelectField.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import BaseForm from "@/components/Layout/BaseForm.vue";
import axios from "@/services/axios";

export default {
  components: { InputField, SelectField, FormSection, BaseForm },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // Objeto reativo para o tipo de pagamento (campo "ativo" removido)
    const tipo = reactive({
      id: null,
      operador_id: '',
      meio_id: '',
      forma_id: '',
      tipo: "",
    });
    const errors = reactive({});
    const isSubmitting = ref(false);

    // Operadores
    const operadores = ref([]);
    const operadoresOptions = computed(() =>
      operadores.value.map(op => ({ text: op.operador, value: op.id }))
    );

    // Meios – serão carregados conforme o operador selecionado
    const meios = ref([]);
    const meiosOptions = computed(() =>
      meios.value.map(m => ({ text: m.meio, value: m.id }))
    );

    // Formas – serão carregadas conforme o meio selecionado e operador
    const formas = ref([]);
    const formasOptions = computed(() =>
      formas.value.map(f => ({ text: f.forma, value: f.id }))
    );

    // Buscar operadores
    const fetchOperadores = async () => {
      try {
        const response = await axios.get('/financeiro/parametros/operadores');
        operadores.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar operadores:", error);
      }
    };

    // Buscar meios filtrados pelo operador selecionado
    const fetchMeios = async (operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/meios', {
          params: { operador_id: operadorId }
        });
        meios.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar meios:", error);
      }
    };

    // Buscar formas filtradas pelo meio e operador selecionados
    const fetchFormas = async (meioId, operadorId) => {
      try {
        const response = await axios.get('/financeiro/parametros/formas', {
          params: { meio_id: meioId, operador_id: operadorId }
        });
        formas.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar formas:", error);
      }
    };

    // Quando o operador muda, limpar meio e forma e buscar os meios relacionados
    const onOperadorChange = (newOperadorId) => {
      tipo.meio_id = '';
      tipo.forma_id = '';
      formas.value = [];
      if (newOperadorId) {
        fetchMeios(newOperadorId);
      } else {
        meios.value = [];
      }
    };

    // Quando o meio muda, limpar forma e buscar as formas filtradas pelo meio e operador
    const onMeioChange = (newMeioId) => {
      tipo.forma_id = '';
      if (newMeioId && tipo.operador_id) {
        fetchFormas(newMeioId, tipo.operador_id);
      } else {
        formas.value = [];
      }
    };

    // Validação do formulário
    const validateForm = () => {
      errors.operador_id = tipo.operador_id ? "" : "Operador é obrigatório.";
      errors.meio_id = tipo.meio_id ? "" : "Meio é obrigatório.";
      errors.forma_id = tipo.forma_id ? "" : "Forma é obrigatória.";
      errors.tipo = tipo.tipo ? "" : "O tipo de pagamento é obrigatório.";
    };

    const isFormValid = computed(() => {
      return tipo.operador_id && tipo.meio_id && tipo.forma_id && tipo.tipo.trim() !== "" &&
             !Object.values(errors).some(error => error);
    });

    const novoTipo = () => {
      Object.assign(tipo, {
        operador_id: '',
        meio_id: '',
        forma_id: '',
        tipo: "",
      });
      for (const key in errors) {
        if (Object.hasOwn(errors, key)) errors[key] = "";
      }
    };

    const salvarTipo = async () => {
      if (isSubmitting.value) return;
      validateForm();
      if (!isFormValid.value) {
        console.error("O formulário contém erros:", errors);
        return;
      }
      isSubmitting.value = true;
      try {
        const method = tipo.id ? "put" : "post";
        const url = tipo.id
          ? `/financeiro/parametros/tipos/${tipo.id}`
          : "/financeiro/parametros/tipos";
        await axios[method](url, {
          operador_id: tipo.operador_id,
          meio_id: tipo.meio_id,
          forma_id: tipo.forma_id,
          tipo: tipo.tipo,
        });
        router.push("/financeiro/parametros/tipos");
      } catch (error) {
        console.error("Erro ao salvar tipo:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const voltar = () => {
      router.push("/financeiro/parametros/tipos");
    };

    onMounted(async () => {
      await fetchOperadores();
      const tipoId = route.params.id;
      if (tipoId) {
        try {
          const response = await axios.get(`/financeiro/parametros/tipos/${tipoId}`);
          Object.assign(tipo, response.data);
          // Após carregar o tipo, carregar os meios e formas encadeados
          if (tipo.operador_id) {
            await fetchMeios(tipo.operador_id);
          }
          if (tipo.meio_id && tipo.operador_id) {
            await fetchFormas(tipo.meio_id, tipo.operador_id);
          }
        } catch (error) {
          console.error("Erro ao carregar tipo:", error);
        }
      } else {
        novoTipo();
      }
    });

    return {
      tipo,
      errors,
      isFormValid,
      isSubmitting,
      salvarTipo,
      voltar,
      route,
      operadoresOptions,
      meiosOptions,
      formasOptions,
      onOperadorChange,
      onMeioChange,
    };
  },
};
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.error-input {
  border-color: #dc3545;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
.form-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* Classe para aumentar a largura mínima dos campos */
.larger-field {
  min-width: 180px; /* Ajuste esse valor conforme necessário */
}
</style>
